import React, { useState, useEffect } from 'react';
import AssignInvalidationModal from './AssignInvalidationModal';
import NotificationSettingsModal from './NotificationSettingsModal';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { formatPrice } from '../utils/formatters';

const InvalidationStrategySection = ({
  assetKind = 'crypto',
  position,
  token,
  portfolioId,
  refetch
}) => {
  const { currentPortfolio } = usePortfolioContext();
  const currency = currentPortfolio?.currency || 'USD';
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  
  const getStoredCollapseState = (sectionName, defaultValue = false) => {
    try {
      const storedValue = localStorage.getItem(`${sectionName}_collapsed`);
      return storedValue !== null ? JSON.parse(storedValue) : defaultValue;
    } catch (e) {
      return defaultValue;
    }
  };

  const [collapsed, setCollapsed] = useState(() => getStoredCollapseState('invalidationStrategy', true));

  useEffect(() => {
    try {
      localStorage.setItem('invalidationStrategy_collapsed', JSON.stringify(collapsed));
    } catch (_) {}
  }, [collapsed]);

  // Use invalidation level from position (loaded with GET_TOKEN_WITH_POSITION)
  const level = position?.invalidationLevel;
  const hasLevel = !!level;
  const loading = false; // Data comes from parent query

  const handleCloseModal = () => {
    setShowAssignModal(false);
    if (refetch) refetch();
  };

  // Determine warning status for each condition
  const getConditionStatus = () => {
    if (!level) return null;
    
    const conditions = [];
    
    // Price condition
    if (level.triggerPrice) {
      const triggered = level.isPriceTriggered;
      const priceDropRemaining = level.priceDropPercentRemaining ? parseFloat(level.priceDropPercentRemaining) : null;
      const isClose = priceDropRemaining !== null && priceDropRemaining <= 10 && !triggered;
      
      // Use backend-calculated drop percentages
      const originalDropPercent = level.originalDropPercent ? parseFloat(level.originalDropPercent) : null;
      const currentDropPercent = level.currentDropPercent ? parseFloat(level.currentDropPercent) : null;
      
      conditions.push({
        type: 'price',
        triggered,
        isClose,
        triggerPrice: parseFloat(level.triggerPrice),
        currentPrice: level.currentPrice ? parseFloat(level.currentPrice) : null,
        remainingPercent: priceDropRemaining,
        originalDropPercent,
        currentDropPercent,
        positionChanged: level.positionChanged
      });
    }
    
    // Time condition
    if (level.timeDurationDays) {
      const triggered = level.isTimeTriggered;
      const daysRemaining = level.daysRemaining;
      const totalDays = level.timeDurationDays;
      const percentRemaining = daysRemaining !== null && totalDays > 0 ? (daysRemaining / totalDays) * 100 : null;
      const isClose = percentRemaining !== null && percentRemaining <= 10 && !triggered;
      
      conditions.push({
        type: 'time',
        triggered,
        isClose,
        totalDays,
        daysHeld: level.daysHeld,
        daysRemaining
      });
    }
    
    // Overall status
    const anyTriggered = conditions.some(c => c.triggered);
    const anyClose = conditions.some(c => c.isClose);
    
    return { conditions, anyTriggered, anyClose };
  };

  const statusInfo = getConditionStatus();

  // Header warning icon - colored circles matching ExitStrategySection
  const renderHeaderWarning = () => {
    if (!statusInfo) return null;
    
    if (statusInfo.anyTriggered) {
      return (
        <span 
          className="ml-2 w-3 h-3 rounded-full bg-red-500 inline-block" 
          title="Invalidation triggered"
        />
      );
    }
    if (statusInfo.anyClose) {
      return (
        <span 
          className="ml-2 w-3 h-3 rounded-full bg-yellow-500 inline-block" 
          title="Close to invalidation"
        />
      );
    }
    return null;
  };

  // Render condition block
  const renderConditionBlock = (condition) => {
    const bgClass = condition.triggered 
      ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
      : condition.isClose
        ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
        : 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600';
    
    if (condition.type === 'price') {
      const icon = condition.triggered ? '🔴' : condition.isClose ? '🟡' : '💰';
      const priceLabel = formatPrice(condition.triggerPrice, currency);
      
      // Calculate current price difference from average buy price
      const averageBuyPrice = position?.averageBuyPrice ? parseFloat(position.averageBuyPrice) : null;
      let currentStatusText = '';
      if (condition.currentPrice !== null && averageBuyPrice && averageBuyPrice > 0) {
        const currentDiff = ((condition.currentPrice - averageBuyPrice) / averageBuyPrice) * 100;
        if (currentDiff < 0) {
          currentStatusText = `Currently ${Math.abs(currentDiff).toFixed(1)}% lower`;
        } else if (currentDiff > 0) {
          currentStatusText = `Currently ${currentDiff.toFixed(1)}% higher`;
        } else {
          currentStatusText = 'Currently at avg. buy price';
        }
      }
      
      // Strategy text: use original drop percent if available (from template), otherwise current
      let strategyText;
      if (condition.originalDropPercent !== null) {
        strategyText = `Price invalidation: ${condition.originalDropPercent.toFixed(0)}% drop from average buy price`;
      } else if (condition.currentDropPercent !== null) {
        strategyText = `Price invalidation: ${condition.currentDropPercent.toFixed(0)}% drop from average buy price`;
      } else {
        strategyText = `Price invalidation (price < ${priceLabel})`;
      }
      
      // Current status text: "Currently xx% lower/higher, trigger: price < $Y"
      let statusLine = condition.currentPrice !== null && currentStatusText
        ? `${currentStatusText}, trigger: price < ${priceLabel}`
        : `Trigger: price < ${priceLabel}`;
      
      // Add position changed warning to status line
      if (condition.positionChanged && condition.originalDropPercent !== null && condition.currentDropPercent !== null) {
        statusLine = `⚠️ Was ${condition.originalDropPercent.toFixed(0)}%, now ${condition.currentDropPercent.toFixed(0)}% from avg. buy. Trigger: price < ${priceLabel}`;
      }
      
      let statusText;
      if (condition.triggered) {
        statusText = 'Triggered';
      } else if (condition.remainingPercent !== null) {
        statusText = `${condition.remainingPercent.toFixed(1)}% to drop`;
      } else {
        statusText = 'Watching';
      }
      
      // Use orange background if position changed
      const blockBgClass = condition.positionChanged 
        ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800'
        : bgClass;
      
      return (
        <div className={`flex-1 p-2 rounded ${blockBgClass}`}>
          <div className="flex items-start justify-between mb-1">
            <div className="flex items-start flex-1">
              <span className="mr-2">{icon}</span>
              <div className="flex flex-col">
                <span className={`text-sm ${condition.triggered ? 'text-red-700 dark:text-red-300' : condition.isClose ? 'text-yellow-700 dark:text-yellow-300' : 'text-gray-700 dark:text-gray-300'}`}>
                  {strategyText}
                </span>
                {statusLine && (
                  <span className={`text-xs mt-0.5 ${condition.positionChanged ? 'text-orange-600 dark:text-orange-400' : condition.triggered ? 'text-red-600 dark:text-red-400' : condition.isClose ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    {statusLine}
                  </span>
                )}
              </div>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded ml-2 ${
              condition.triggered 
                ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                : condition.isClose
                  ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
            }`}>
              {statusText}
            </span>
          </div>
        </div>
      );
    }
    
    if (condition.type === 'time') {
      const icon = condition.triggered ? '🔴' : condition.isClose ? '🟡' : '⏱️';
      
      // Strategy text: "Time invalidation: hold longer than xx days"
      const strategyText = `Time invalidation: hold longer than ${condition.totalDays} days`;
      
      // Current status text: "Currently holding for xx days"
      const statusLine = condition.daysHeld !== null
        ? `Currently holding for ${condition.daysHeld} days`
        : null;
      
      let statusText;
      if (condition.triggered) {
        statusText = 'Triggered';
      } else if (condition.daysRemaining !== null) {
        statusText = `${condition.daysRemaining}d left`;
      } else {
        statusText = 'Watching';
      }
      
      return (
        <div className={`flex-1 p-2 rounded ${bgClass}`}>
          <div className="flex items-start justify-between mb-1">
            <div className="flex items-start flex-1">
              <span className="mr-2">{icon}</span>
              <div className="flex flex-col">
                <span className={`text-sm ${condition.triggered ? 'text-red-700 dark:text-red-300' : condition.isClose ? 'text-yellow-700 dark:text-yellow-300' : 'text-gray-700 dark:text-gray-300'}`}>
                  {strategyText}
                </span>
                {statusLine && (
                  <span className={`text-xs mt-0.5 ${condition.triggered ? 'text-red-600 dark:text-red-400' : condition.isClose ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    {statusLine}
                  </span>
                )}
              </div>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded ml-2 ${
              condition.triggered 
                ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                : condition.isClose
                  ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
            }`}>
              {statusText}
            </span>
          </div>
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="bg-gray-100 dark:bg-gray-800 rounded-lg shadow-md mb-4 overflow-hidden border border-gray-200 dark:border-gray-700">
      <div 
        className="flex justify-between items-center py-2 px-3 cursor-pointer bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600"
        onClick={() => setCollapsed(!collapsed)}
      >
        <h2 className="font-semibold text-base flex items-center">
          Invalidation Strategy
          {hasLevel && (
            <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
              — {level.templateName || 'Custom'}
            </span>
          )}
          {renderHeaderWarning()}
          {level?.isModified && (
            <span className="ml-1 px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-xs rounded">
              Modified
            </span>
          )}
          {level?.positionChanged && !level?.isModified && (
            <span className="ml-1 px-2 py-0.5 bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 text-xs rounded" title="Position has changed since invalidation was set">
              Position changed
            </span>
          )}
          {!hasLevel && !loading && (
            <span className="ml-2 text-sm font-normal text-gray-400">— Not set</span>
          )}
        </h2>
        <div className="flex items-center space-x-2">
          {hasLevel && (
            <button 
              type="button"
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 focus:outline-none"
              onClick={(e) => { e.stopPropagation(); setNotificationModalOpen(true); }}
              title="Notification settings"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
              </svg>
            </button>
          )}
          <button 
            type="button"
            className="bg-blue-500 hover:bg-blue-700 text-white text-xs font-semibold py-1 px-2 rounded"
            onClick={(e) => { e.stopPropagation(); setShowAssignModal(true); }}
          >
            {hasLevel ? 'Edit' : 'Set'}
          </button>
          <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            {collapsed ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        </div>
      </div>
      
      {!collapsed && (
        <div className="p-3">
          {loading ? (
            <div className="text-center py-4 text-gray-500">Loading...</div>
          ) : hasLevel && statusInfo ? (
            <div className="space-y-3">
              {/* Conditions - side by side if both present */}
              <div className={`flex gap-2 ${statusInfo.conditions.length === 1 ? 'flex-col' : 'flex-row'}`}>
                {statusInfo.conditions.map((condition, idx) => (
                  <React.Fragment key={idx}>
                    {renderConditionBlock(condition)}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                Define conditions that signal when a position thesis is no longer valid.
              </p>
              <button
                onClick={() => setShowAssignModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                Set Invalidation Strategy
              </button>
            </div>
          )}
        </div>
      )}

      {showAssignModal && (
        <AssignInvalidationModal assetKind={assetKind}
          portfolioId={portfolioId}
          tokenId={token?.id}
          tokenName={token?.name || token?.ticker}
          currentLevel={level}
          averageBuyPrice={position?.averageBuyPrice}
          onClose={handleCloseModal}
        />
      )}
      <NotificationSettingsModal 
        isOpen={notificationModalOpen} 
        onClose={() => setNotificationModalOpen(false)} 
      />
    </div>
  );
};

export default InvalidationStrategySection;
